import { Drawer, IconButton, styled } from '@ethings/ui-toolkit';
import { Box, useTheme } from '@mui/material';
import { FC, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../icons';

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: 0,
  color: theme.palette.grey[500],
}));

interface Props {
  buttonElement: ReactNode;
  children: ReactNode;
  onClose: () => void;
  open: boolean;
}

const MODAL_DRAWER_CONTAINER_ID = 'modal-inlay-map-layer-selection-root';

export const LayerMenuDesktop: FC<Props> = ({ buttonElement, children, onClose, open }) => {
  const theme = useTheme();
  const isInsideFullscreenModal = typeof document !== 'undefined' && !!document.getElementById(MODAL_DRAWER_CONTAINER_ID);

  const drawerContent = (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      hideBackdrop
      transitionDuration={250}
      ModalProps={{
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disableRestoreFocus: true,
        sx: { pointerEvents: 'none' },
        style: { zIndex: isInsideFullscreenModal ? 1401 : undefined },
      }}
      PaperProps={{
        sx: {
          py: 2,
          px: 3,
          pointerEvents: 'auto',
          color: theme.palette.getContrastText(theme.palette.background.paper),
          width: 350,
          cursor: 'default',
          scrollbarWidth: 'thin',
          '& *:focus-visible': {
            outline: `1px solid ${theme.palette.primary.main}`,
          },
          '& .Mui-focusVisible': {
            outline: `1px solid ${theme.palette.primary.main}`,
          },
          '&::-webkit-scrollbar': {
            width: theme.scrollbar.width,
            height: theme.scrollbar.height,
            backgroundColor: theme.palette.common.white,
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.grey[300],
            borderRadius: theme.scrollbar.borderRadius,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.primary.main,
            borderRadius: theme.scrollbar.borderRadius,
          },
        },
      }}
    >
      <StyledIconButton data-testid="close-layer-selection-button" aria-label="close" onClick={onClose}>
        <Icon data-testid="close-icon" icon="CloseIcon" />
      </StyledIconButton>
      {children}
    </Drawer>
  );

  return (
    <Box>
      {buttonElement}
      {createPortal(drawerContent, document.body)}
    </Box>
  );
};
